import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { T } from '../../assets/theme';

export default function RunwayProfileChart({ points, thresholds, notAvailable, adElevM }) {
  if (!thresholds || thresholds.length < 1) return null;

  let chartData = [];
  if (notAvailable || !points || points.length === 0) {
    // If not available, we use thresholds to draw a simple line
    if (thresholds.length < 2) return null;
    
    const t1 = thresholds[0];
    const t2 = thresholds[thresholds.length - 1];
    
    // Simple Haversine approximation or just use a fixed 3km if no coords
    let dist = 3000;
    const lat1 = parseFloat(t1.thr_lat || t1.lat);
    const lon1 = parseFloat(t1.thr_lon || t1.lon);
    const lat2 = parseFloat(t2.thr_lat || t2.lat);
    const lon2 = parseFloat(t2.thr_lon || t2.lon);

    if (!isNaN(lat1) && !isNaN(lon1) && !isNaN(lat2) && !isNaN(lon2)) {
        const R = 6371e3;
        const f1 = lat1 * Math.PI/180;
        const f2 = lat2 * Math.PI/180;
        const df = (lat2 - lat1) * Math.PI/180;
        const dl = (lon2 - lon1) * Math.PI/180;
        const a = Math.sin(df/2) * Math.sin(df/2) + Math.cos(f1) * Math.cos(f2) * Math.sin(dl/2) * Math.sin(dl/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        dist = R * c;
    }
    
    chartData = [
      { distance_m: 0, elevation_m: parseFloat(t1.elev_m || t1.elevM || 0), name: t1.threshold || t1.id },
      { distance_m: dist, elevation_m: parseFloat(t2.elev_m || t2.elevM || 0), name: t2.threshold || t2.id } 
    ];
  } else {
    chartData = points.map((p, i) => ({
      distance_m: p.distance_m,
      elevation_m: p.elevation_m,
      name: `PROF45/${i * 2 || 0}`
    }));
  }

  const maxThrElev = Math.max(...thresholds.map(t => parseFloat(t.elev_m || t.elevM || 0)));
  const baseElev = (adElevM != null && !isNaN(adElevM)) ? adElevM : maxThrElev;
  const ihsElev = baseElev + 45;

  const finalData = chartData.map(d => ({
    ...d,
    ihs: ihsElev
  }));

  const titleRunways = thresholds.map(t => t.threshold || t.id).filter(Boolean).join('/') || 'PROFILE';

  return (
    <div style={{ width: '100%', height: 400, background: '#ffffff', padding: '20px 20px 10px 0', border: `1px solid #eee`, marginTop: 20, marginBottom: 20 }}>
      <div style={{ fontSize: 22, fontWeight: 500, marginBottom: 15, color: '#444', textAlign: 'center', fontFamily: 'sans-serif' }}>
        RWY PROFILE {titleRunways}
      </div>
      <ResponsiveContainer width="100%" height="85%">
        <LineChart data={finalData} margin={{ top: 20, right: 30, left: 10, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eaeaea" vertical={false} />
          <XAxis 
            dataKey="name" 
            stroke="#666" 
            fontSize={12}
            angle={-45}
            textAnchor="end"
            height={60}
            tickMargin={10}
            interval="preserveStartEnd"
          />
          <YAxis 
            stroke="#666" 
            fontSize={12} 
            domain={['dataMin - 10', 'dataMax + 10']}
            tickFormatter={(val) => val.toFixed(3)}
            width={85}
          />
          <Tooltip 
            contentStyle={{ background: '#fff', border: `1px solid #ccc`, fontSize: 14, color: '#333' }}
            itemStyle={{ fontSize: 14 }}
            formatter={(value) => parseFloat(value).toFixed(3)}
          />
          <Legend verticalAlign="bottom" height={20} wrapperStyle={{ fontSize: '13px', paddingTop: '20px' }} />
          <Line 
            type="monotone" 
            dataKey="elevation_m" 
            name="Runway Profile" 
            stroke="#005b82" 
            strokeWidth={3} 
            dot={false} 
          />
          <Line 
            type="step" 
            dataKey="ihs" 
            name="IHS (+45m)" 
            stroke="#d32f2f" 
            strokeWidth={2} 
            strokeDasharray="5 5" 
            dot={false} 
          />
        </LineChart>
      </ResponsiveContainer>
      <div style={{ fontSize: 11, color: '#888', marginTop: 10, textAlign: 'center' }}>
        {notAvailable ? "Note: Using threshold points as profile data is not available." : "Longitudinal profile based on survey data."}
      </div>
    </div>
  );
}
